import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react'
import { MappedLeadData } from '../../../types'
import { LeadReportModal } from '../../../components/LeadReportModal'
import { SkeletonLeadModal } from '../../../components/atoms/Skeleton'
import { useAuth } from '../../../contexts/AuthContext'
import { useClientProfile } from '../../../hooks/useClientProfile'
import { appwriteQueryFns } from '../../../services/query/appwriteQueryFns'
import { AppLayout } from '../../../components/layouts/AppLayout'
import BuyCreditsModal from '../../../components/modals/BuyCreditsModal'
import InvoicesModal from '../../../components/modals/InvoicesModal'

// Valid tab names for lead modal
type LeadTab = 'overview' | 'person' | 'company' | 'signals' | 'network' | 'profile-intel' | 'outreach' | 'interactions'

interface LeadSearchParams {
  tab?: LeadTab
  campaignId?: string // Now required to load lead details
}

export const Route = createFileRoute('/leads/$leadId')({
  validateSearch: (search: Record<string, unknown>): LeadSearchParams => ({
    tab: (search.tab as LeadTab) ?? 'overview',
    campaignId: search.campaignId as string | undefined,
  }),
  component: DirectLeadPage,
})

function DirectLeadPage() {
  const navigate = useNavigate()
  const { leadId } = Route.useParams()
  const { campaignId } = Route.useSearch()
  const { currentUser, targetEmail: impersonatedEmail } = useAuth()
  // Use impersonated email if set (admin viewing as another user), otherwise current user's email
  const targetEmail = impersonatedEmail || currentUser?.email
  const clientQuery = useClientProfile(targetEmail)

  const [lead, setLead] = useState<MappedLeadData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false)
  const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false)

  // Load lead data using campaignId from URL search params
  useEffect(() => {
    const loadLead = async () => {
      if (!leadId || !targetEmail) return

      // Campaign ID is now required to load lead details
      if (!campaignId) {
        setError('Campaign ID required. Please access leads through campaigns.')
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        // Fetch full lead data using campaignId from URL
        const fullLead = await appwriteQueryFns.leads.detail({
          campaignId,
          leadId,
          userEmail: targetEmail,
        })

        if (fullLead) {
          setLead(fullLead)
        } else {
          setError('Lead not found')
        }
      } catch (err: any) {
        console.error('Error loading lead:', err)
        setError(err.message || 'Failed to load lead')
      } finally {
        setIsLoading(false)
      }
    }

    loadLead()
  }, [leadId, campaignId, targetEmail])

  // Handle modal close - navigate to campaigns
  const handleClose = () => {
    // If lead has a campaign, go to that campaign, otherwise go to campaigns list
    if (lead?.campaignId) {
      navigate({
        to: '/campaigns/$campaignId',
        params: { campaignId: lead.campaignId },
      })
    } else {
      navigate({ to: '/campaigns' })
    }
  }

  // Handle tab change - update URL
  const handleTabChange = (newTab: string) => {
    navigate({
      to: '/leads/$leadId',
      params: { leadId },
      search: { tab: newTab as LeadTab },
    })
  }

  return (
    <AppLayout
      currentView="campaigns"
      onBuyCredits={() => setIsBuyCreditsModalOpen(true)}
      onViewInvoices={() => setIsInvoicesModalOpen(true)}
      aiCredits={clientQuery.data?.credits ?? 0}
      creditsLoading={clientQuery.isPending}
      clientInfo={clientQuery.data}
      isLoadingLead={isLoading}
    >
      {/* Loading State */}
      {isLoading && <SkeletonLeadModal />}

      {/* Error State */}
      {error && !isLoading && (
        <div className="min-h-screen flex items-center justify-center pt-28">
          <div className="text-center">
            <div className="mb-4 p-4 rounded-2xl bg-red-500/5 border border-red-500/10 text-red-500 inline-block">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Error Loading Lead</h2>
            <p className="text-sm text-gray-400 mb-4">{error}</p>
            <button
              onClick={() => navigate({ to: '/campaigns' })}
              className="px-6 py-2 bg-[#5260FF] hover:bg-[#5260FF]/80 text-white rounded-lg text-sm font-medium"
            >
              Back to Campaigns
            </button>
          </div>
        </div>
      )}

      {/* Lead Report Modal */}
      {lead && <LeadReportModal lead={lead} onClose={handleClose} campaignId={campaignId} />}

      {/* Buy Credits Modal */}
      <BuyCreditsModal isOpen={isBuyCreditsModalOpen} onClose={() => setIsBuyCreditsModalOpen(false)} />

      {/* Invoices Modal */}
      <InvoicesModal isOpen={isInvoicesModalOpen} onClose={() => setIsInvoicesModalOpen(false)} />
    </AppLayout>
  )
}
